import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Alert,
  AlertDescription,
} from "@/components/ui";
import { DollarSign, Calendar, CreditCard, AlertCircle, CheckCircle, FileText } from "lucide-react";
import { toast } from "react-hot-toast";
import financeService from "@/services/financeService";
import purchaseRequestService from "@/services/purchaseRequestService";

const PAYMENT_METHODS = [
  { value: "transfer", label: "Bank Transfer" },
  { value: "check", label: "Check" },
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
];

const PaymentRecordingModal = ({ open, onOpenChange, payable, onSuccess, type = "payable" }) => {
  const [loading, setLoading] = useState(false);
  const [loadingPRs, setLoadingPRs] = useState(false);
  const [availablePRs, setAvailablePRs] = useState([]);
  const [formData, setFormData] = useState({
    amount: "",
    payment_date: new Date().toISOString().split("T")[0],
    payment_method: "transfer",
    notes: "",
    purchase_request_id: "",
  });
  const [errors, setErrors] = useState({});

  const outstandingBalance = parseFloat(payable?.amount || 0) - parseFloat(payable?.paid_amount || payable?.received_amount || 0);

  // Fetch available closed PRs when modal opens for payables
  useEffect(() => {
    if (open && type === "payable") {
      fetchAvailablePRs();
    }
  }, [open, type]);

  const fetchAvailablePRs = async () => {
    try {
      setLoadingPRs(true);
      
      console.log('🔍 Fetching PRs in Awaiting Delivery Confirmation stage...');
      
      // Fetch PRs that are awaiting delivery confirmation
      const response = await purchaseRequestService.getAllRequests({
        status: "awaiting_delivery_confirmation"
      });
      
      console.log('📦 Full PR Response:', JSON.stringify(response.data, null, 2));
      console.log('📦 Response.data:', response.data);
      console.log('📦 Response.data.data:', response.data.data);
      console.log('📦 Response.data.success:', response.data.success);
      
      let allEligiblePRs = [];
      
      if (response.data.success) {
        console.log('✅ Response is successful, checking data structure...');
        
        // Check if requests is at root level
        if (Array.isArray(response.data.requests)) {
          console.log('📋 Data.requests is array:', response.data.requests.length);
          allEligiblePRs = response.data.requests;
        } else if (Array.isArray(response.data.data)) {
          console.log('📋 Data is array:', response.data.data.length);
          allEligiblePRs = response.data.data;
        } else if (response.data.data && Array.isArray(response.data.data.requests)) {
          console.log('📋 Data.data.requests is array:', response.data.data.requests.length);
          allEligiblePRs = response.data.data.requests;
        } else if (response.data.data && response.data.data.purchaseRequests) {
          console.log('📋 Data.purchaseRequests exists');
          allEligiblePRs = response.data.data.purchaseRequests;
        } else {
          console.log('⚠️ Unknown data structure:', typeof response.data.data, response.data.data);
        }
      } else {
        console.log('❌ Response success is false');
      }
      
      console.log(`✅ Total eligible PRs: ${allEligiblePRs.length}`, allEligiblePRs);
      
      // Fetch all payables to check which PRs are already linked
      const payablesResponse = await financeService.getPayables({});
      const allPayables = payablesResponse.data.success ? payablesResponse.data.data.payables || [] : [];
      
      console.log(`💰 Found ${allPayables.length} payables`, allPayables);
      
      // Get list of PR IDs that are already linked to payables
      const linkedPRIds = new Set();
      allPayables.forEach(p => {
        if (p.purchase_request_id) {
          console.log(`🔗 Payable ${p.id} is linked to PR ${p.purchase_request_id}`);
          linkedPRIds.add(p.purchase_request_id);
        }
      });
      
      console.log(`🔗 Linked PR IDs (${linkedPRIds.size}):`, Array.from(linkedPRIds));
      
      // Filter out PRs that are already linked
      const unlinkedPRs = allEligiblePRs.filter(pr => {
        const isLinked = linkedPRIds.has(pr.id);
        console.log(`PR ${pr.id} (${pr.request_id}): ${isLinked ? 'LINKED' : 'AVAILABLE'}`);
        return !isLinked;
      });
      
      console.log(`✨ Available unlinked PRs: ${unlinkedPRs.length}`, unlinkedPRs);
      
      setAvailablePRs(unlinkedPRs);
    } catch (error) {
      console.error("❌ Error fetching available PRs:", error);
      console.error("Error details:", error.response?.data);
      // Don't show error toast if it's just that there are no PRs
      if (error.response?.status !== 404) {
        toast.error("Failed to load purchase requests");
      }
    } finally {
      setLoadingPRs(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = "Payment amount must be greater than zero";
    }

    if (parseFloat(formData.amount) > outstandingBalance) {
      newErrors.amount = `Payment amount cannot exceed outstanding balance of ${financeService.formatCurrency(outstandingBalance)}`;
    }

    if (!formData.payment_date) {
      newErrors.payment_date = "Payment date is required";
    }

    if (!formData.payment_method) {
      newErrors.payment_method = "Payment method is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // recorded_by references employees.id, not users.id
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      const recorded_by = currentUser.Employee?.id || currentUser.id || 1;

      const paymentData = {
        ...formData,
        amount: parseFloat(formData.amount),
        recorded_by,
      };

      // Include purchase_request_id if selected (for payables)
      if (type === "payable" && formData.purchase_request_id) {
        paymentData.purchase_request_id = parseInt(formData.purchase_request_id);
      }

      if (type === "payable") {
        await financeService.recordPayablePayment(payable.id, paymentData);
      } else {
        await financeService.recordReceivablePayment(payable.id, paymentData);
      }

      onSuccess();
    } catch (error) {
      console.error("Error recording payment:", error);
      toast.error(error.response?.data?.message || "Failed to record payment");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="space-y-6">
          {/* Header */}
          <div className="relative border-b pb-6">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-t-lg"></div>
            <div className="flex items-start gap-4 mt-2">
              <div className="flex-1">
                <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  Record Payment {type === "receivable" ? "Received" : ""}
                </h2>
                <p className="text-gray-600 mt-2 text-base">
                  {type === "payable" 
                    ? `Record payment to ${payable?.vendor_name || payable?.beneficiary_name}`
                    : `Record payment received from ${payable?.client_name}`
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Outstanding Balance Alert */}
          <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl"></div>
            <div className="relative bg-white/60 backdrop-blur-sm border-2 border-blue-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                    <AlertCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-600 uppercase tracking-wider">Outstanding Balance</p>
                    <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                      {financeService.formatCurrency(outstandingBalance)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Payment Amount */}
            <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
              <Label htmlFor="amount" className="text-sm font-bold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-blue-600" />
                Payment Amount <span className="text-red-500">*</span>
              </Label>
              <div className="relative mt-3">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="text-gray-500 font-bold text-lg">₦</span>
                </div>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={outstandingBalance}
                  value={formData.amount}
                  onChange={(e) => handleChange("amount", e.target.value)}
                  className={`pl-12 pr-4 h-14 text-lg font-semibold border-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-lg ${errors.amount ? "border-red-500" : "border-gray-300"}`}
                  placeholder="0.00"
                />
              </div>
              {errors.amount && (
                <div className="flex items-center gap-2 mt-2 text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  <p className="text-sm font-medium">{errors.amount}</p>
                </div>
              )}
              <button
                type="button"
                onClick={() => handleChange("amount", outstandingBalance.toFixed(2))}
                className="mt-3 text-sm font-semibold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Pay full balance ({financeService.formatCurrency(outstandingBalance)})
              </button>
            </div>

            {/* Purchase Request Selection (Payables Only) */}
            {type === "payable" && (
              <div className="bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200 rounded-xl p-5 shadow-sm">
                <Label htmlFor="purchase_request_id" className="text-sm font-bold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                  <FileText className="h-4 w-4 text-purple-600" />
                  Link to Purchase Request (Optional)
                </Label>
                <div className="relative mt-3">
                  <Select
                    value={formData.purchase_request_id}
                    onValueChange={(value) => handleChange("purchase_request_id", value)}
                    disabled={loadingPRs}
                  >
                    <SelectTrigger className={`h-12 border-2 ${errors.purchase_request_id ? "border-red-500" : "border-gray-300"} focus:border-purple-500`}>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <SelectValue placeholder={loadingPRs ? "Loading purchase requests..." : "Select a purchase request"} />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">None - Manual Payment</SelectItem>
                      {availablePRs.map((pr) => (
                        <SelectItem key={pr.id} value={pr.id.toString()}>
                          <div className="flex flex-col py-1">
                            <span className="font-semibold">{pr.request_id}</span>
                            <span className="text-xs text-gray-500">{pr.item_description?.substring(0, 60)}{pr.item_description?.length > 60 ? '...' : ''}</span>
                            <span className="text-xs font-semibold text-blue-600">{financeService.formatCurrency(pr.total_amount)}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {availablePRs.length === 0 && !loadingPRs && (
                  <div className="flex items-center gap-2 mt-2 text-gray-500 bg-gray-50 p-2 rounded-lg">
                    <AlertCircle className="h-4 w-4" />
                    <p className="text-xs font-medium">No closed purchase requests available for linking</p>
                  </div>
                )}
                {availablePRs.length > 0 && (
                  <div className="flex items-center gap-2 mt-2 text-purple-700 bg-purple-50 p-2 rounded-lg">
                    <CheckCircle className="h-4 w-4" />
                    <p className="text-xs font-semibold">{availablePRs.length} closed purchase request(s) available</p>
                  </div>
                )}
                {errors.purchase_request_id && (
                  <div className="flex items-center gap-2 mt-2 text-red-600">
                    <AlertCircle className="h-4 w-4" />
                    <p className="text-sm font-medium">{errors.purchase_request_id}</p>
                  </div>
                )}
              </div>
            )}

            {/* Payment Date & Method - Side by Side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Payment Date */}
              <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-xl p-5 shadow-sm">
                <Label htmlFor="payment_date" className="text-sm font-bold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-green-600" />
                  Payment Date <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-3">
                  <Input
                    id="payment_date"
                    type="date"
                    value={formData.payment_date}
                    onChange={(e) => handleChange("payment_date", e.target.value)}
                    className={`h-12 border-2 ${errors.payment_date ? "border-red-500" : "border-gray-300"} focus:border-green-500 focus:ring-2 focus:ring-green-200 rounded-lg`}
                  />
                </div>
                {errors.payment_date && (
                  <div className="flex items-center gap-2 mt-2 text-red-600">
                    <AlertCircle className="h-4 w-4" />
                    <p className="text-sm font-medium">{errors.payment_date}</p>
                  </div>
                )}
              </div>

              {/* Payment Method */}
              <div className="bg-gradient-to-br from-indigo-50 to-white border-2 border-indigo-200 rounded-xl p-5 shadow-sm">
                <Label htmlFor="payment_method" className="text-sm font-bold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-indigo-600" />
                  Payment Method <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-3">
                  <Select
                    value={formData.payment_method}
                    onValueChange={(value) => handleChange("payment_method", value)}
                  >
                    <SelectTrigger className={`h-12 border-2 ${errors.payment_method ? "border-red-500" : "border-gray-300"} focus:border-indigo-500`}>
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-gray-400" />
                        <SelectValue placeholder="Select payment method" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map((method) => (
                        <SelectItem key={method.value} value={method.value}>
                          {method.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {errors.payment_method && (
                  <div className="flex items-center gap-2 mt-2 text-red-600">
                    <AlertCircle className="h-4 w-4" />
                    <p className="text-sm font-medium">{errors.payment_method}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-xl p-5 shadow-sm">
              <Label htmlFor="notes" className="text-sm font-bold text-gray-700 uppercase tracking-wide">
                Notes (Optional)
              </Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                placeholder="Add any additional notes about this payment..."
                rows={4}
                maxLength={500}
                className="mt-3 border-2 border-gray-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 rounded-lg resize-none"
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-gray-500 italic">Optional payment details or reference information</p>
                <p className="text-xs font-semibold text-gray-600">
                  {formData.notes.length}/500
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t-2 border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
                className="px-6 py-3 h-12 border-2 border-gray-300 hover:bg-gray-50 font-semibold"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="px-8 py-3 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold shadow-lg hover:shadow-xl transition-all duration-200"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Recording...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Record Payment
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentRecordingModal;
